import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { type Board, type BoardsListOptions, type Library, SortDirection, type SubnavBoardSort } from '@air/api/types';

import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';

export const BOARD_SUBBOARDS = 'BOARD_SUBBOARDS';

export const getBoardSubBoardsKey = (boardId: Board['id'], sortField?: SubnavBoardSort, libraryId?: Library['id']) => {
  return [BOARD_SUBBOARDS, { boardId, sortField, libraryId }];
};

export const isBoardSubBoardsKey = (queryKey: QueryKey, libraryId?: Library['id']): boolean => {
  if (!Array.isArray(queryKey) || queryKey[0] !== BOARD_SUBBOARDS) {
    return false;
  }

  const keyData = queryKey[1];
  if (typeof keyData !== 'object' || keyData === null) {
    return false;
  }

  // If libraryId is provided, check if the query key matches that library
  if (libraryId !== undefined) {
    return 'libraryId' in keyData && keyData.libraryId === libraryId;
  }

  // If no libraryId provided, match any board sub-boards key
  return true;
};

export type UseSubBoardsListParams = {
  boardId: string;
  canFetchData: boolean;
  libraryId?: Library['id'];
  params?: Partial<BoardsListOptions>;
};

export const useSubBoardsList = ({ boardId, canFetchData, libraryId, params }: UseSubBoardsListParams) => {
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  // Sub-boards within a library use the library's sort, otherwise use workspace sort
  const subnavSort = getSubNavSortValue(libraryId);
  const sortField = libraryId ? subnavSort?.libraryBoardSorts?.[0] : subnavSort?.boardSort;

  const inifiteQuery = useInfiniteQuery({
    queryKey: getBoardSubBoardsKey(boardId, sortField, libraryId),
    queryFn: ({ pageParam }) =>
      privateBoardsFetcher({
        params: {
          isMemberOfBoard: 'ALL',
          view: boardId,
          numThumbnails: 0,
          includeAncestors: true,
          sortField: sortField ? { name: sortField.name, direction: sortField.direction } : undefined,
          ...params,
        },
        sortFieldName: sortField?.name || 'customSort',
        sortDirection: sortField?.direction || SortDirection.desc,
        cursor: pageParam || null,
      }),
    enabled: !!boardId && canFetchData,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });

  return {
    data: inifiteQuery.data?.pages.flatMap((d) => d.data) ?? [],
    hasMore: !!inifiteQuery.hasNextPage,
    loadNextPage: inifiteQuery.fetchNextPage,
    isLoadingMore: inifiteQuery.isFetchingNextPage,
    isInitialLoading: inifiteQuery.isLoading,
  };
};
